import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    GetPanelPlacementByPoiParams,
    GetPanelPlacementByPoiResponse,
    GetPanelPlacementParams,
    GetPanelPlacementResponse,
    ProblemDetails,
    SolveAnchorPlacementModel,
    SolveAnchorPlacementResponse,
    SolveConnectorPlacementModel,
    SolveConnectorPlacementResponse,
    SolveCornerPlacementModel,
    SolveCornerPlacementResponse,
    SolvePanelPlacementModel,
    SolvePanelPlacementResponse
} from '../../models';

import { useCustomAppClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
    const result = { queryKey } as T & { queryKey: K };
    for (const key of Object.keys(query)) {
        // The explicit queryKey always wins, matching the previous
        // `{ ...query, queryKey }` spread where it was set last.
        if (key === 'queryKey') continue;
        Object.defineProperty(result, key, {
            enumerable: true,
            configurable: true,
            get: () => (query as Record<string, unknown>)[key]
        });
    }
    return result;
};

/**
 * Get Panel Placement By PoiId
 */
export const useGetPanelPlacementByPoiHook = () => {
    const getPanelPlacementByPoi = useCustomAppClient<GetPanelPlacementByPoiResponse>();

    return useCallback(
        (poiId: string, params: GetPanelPlacementByPoiParams, signal?: AbortSignal) => {
            return getPanelPlacementByPoi({
                url: `/forming/panelplacementByPoiId/${poiId}`,
                method: 'GET',
                params,
                signal
            });
        },
        [getPanelPlacementByPoi]
    );
};

export const getGetPanelPlacementByPoiQueryKey = (poiId: string, params?: GetPanelPlacementByPoiParams) => {
    return [`/forming/panelplacementByPoiId/${poiId}`, ...(params ? [params] : [])] as const;
};

export const useGetPanelPlacementByPoiQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    poiId: string,
    params: GetPanelPlacementByPoiParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>, TError, TData>
        >;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetPanelPlacementByPoiQueryKey(poiId, params);

    const getPanelPlacementByPoi = useGetPanelPlacementByPoiHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>> = ({
        signal
    }) => getPanelPlacementByPoi(poiId, params, signal);

    return { queryKey, queryFn, enabled: poiId !== null && poiId !== undefined, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPanelPlacementByPoiQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>
>;
export type GetPanelPlacementByPoiQueryError = ErrorType<ProblemDetails>;

export function useGetPanelPlacementByPoi<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    poiId: string,
    params: GetPanelPlacementByPoiParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPanelPlacementByPoi<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    poiId: string,
    params: GetPanelPlacementByPoiParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPanelPlacementByPoi<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    poiId: string,
    params: GetPanelPlacementByPoiParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetPanelPlacementByPoi<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    poiId: string,
    params: GetPanelPlacementByPoiParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementByPoiHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetPanelPlacementByPoiQueryOptions(poiId, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Get Panel Placements for all valid Pois
 */
export const useGetPanelPlacementHook = () => {
    const getPanelPlacement = useCustomAppClient<GetPanelPlacementResponse>();

    return useCallback(
        (params: GetPanelPlacementParams, signal?: AbortSignal) => {
            return getPanelPlacement({ url: `/forming/panelplacement`, method: 'GET', params, signal });
        },
        [getPanelPlacement]
    );
};

export const getGetPanelPlacementQueryKey = (params?: GetPanelPlacementParams) => {
    return [`/forming/panelplacement`, ...(params ? [params] : [])] as const;
};

export const useGetPanelPlacementQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetPanelPlacementParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>, TError, TData>
        >;
    }
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetPanelPlacementQueryKey(params);

    const getPanelPlacement = useGetPanelPlacementHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>> = ({ signal }) =>
        getPanelPlacement(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPanelPlacementQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>
>;
export type GetPanelPlacementQueryError = ErrorType<ProblemDetails>;

export function useGetPanelPlacement<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetPanelPlacementParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPanelPlacement<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetPanelPlacementParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPanelPlacement<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetPanelPlacementParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetPanelPlacement<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>,
    TError = ErrorType<ProblemDetails>
>(
    params: GetPanelPlacementParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPanelPlacementHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetPanelPlacementQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Solve panel placement using existing locked placements, unsaved groups, saved prefab groups and material-list constraints
 */
export const useSolvePanelPlacementHook = () => {
    const solvePanelPlacement = useCustomAppClient<SolvePanelPlacementResponse>();

    return useCallback(
        (solvePanelPlacementModel: SolvePanelPlacementModel, signal?: AbortSignal) => {
            return solvePanelPlacement({
                url: `/forming/solve-panel-placement`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: solvePanelPlacementModel,
                signal
            });
        },
        [solvePanelPlacement]
    );
};

export const getSolvePanelPlacementMutationKey = () => ['solvePanelPlacement'] as const;

export const useSolvePanelPlacementMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useSolvePanelPlacementHook>>>,
        TError,
        SolvePanelPlacementMutationVariables,
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSolvePanelPlacementHook>>>,
    TError,
    SolvePanelPlacementMutationVariables,
    TContext
> => {
    const mutationKey = getSolvePanelPlacementMutationKey();
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const solvePanelPlacement = useSolvePanelPlacementHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useSolvePanelPlacementHook>>>,
        SolvePanelPlacementMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return solvePanelPlacement(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type SolvePanelPlacementMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useSolvePanelPlacementHook>>>
>;
export type SolvePanelPlacementMutationBody = SolvePanelPlacementModel;
export type SolvePanelPlacementMutationError = ErrorType<ProblemDetails>;
export type SolvePanelPlacementMutationVariables = { data: SolvePanelPlacementModel };

export const useSolvePanelPlacement = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useSolvePanelPlacementHook>>>,
            TError,
            SolvePanelPlacementMutationVariables,
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useSolvePanelPlacementHook>>>,
    TError,
    SolvePanelPlacementMutationVariables,
    TContext
> => {
    return useMutation(useSolvePanelPlacementMutationOptions(options), queryClient);
};
/**
 * Solve automatic or manual corner placement on a selected corner POI
 */
export const useSolveCornerPlacementHook = () => {
    const solveCornerPlacement = useCustomAppClient<SolveCornerPlacementResponse>();

    return useCallback(
        (solveCornerPlacementModel: SolveCornerPlacementModel, signal?: AbortSignal) => {
            return solveCornerPlacement({
                url: `/forming/solve-corner-placement`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: solveCornerPlacementModel,
                signal
            });
        },
        [solveCornerPlacement]
    );
};

export const getSolveCornerPlacementMutationKey = () => ['solveCornerPlacement'] as const;

export const useSolveCornerPlacementMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useSolveCornerPlacementHook>>>,
        TError,
        SolveCornerPlacementMutationVariables,
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSolveCornerPlacementHook>>>,
    TError,
    SolveCornerPlacementMutationVariables,
    TContext
> => {
    const mutationKey = getSolveCornerPlacementMutationKey();
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const solveCornerPlacement = useSolveCornerPlacementHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useSolveCornerPlacementHook>>>,
        SolveCornerPlacementMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return solveCornerPlacement(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type SolveCornerPlacementMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useSolveCornerPlacementHook>>>
>;
export type SolveCornerPlacementMutationBody = SolveCornerPlacementModel;
export type SolveCornerPlacementMutationError = ErrorType<ProblemDetails>;
export type SolveCornerPlacementMutationVariables = { data: SolveCornerPlacementModel };

export const useSolveCornerPlacement = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useSolveCornerPlacementHook>>>,
            TError,
            SolveCornerPlacementMutationVariables,
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useSolveCornerPlacementHook>>>,
    TError,
    SolveCornerPlacementMutationVariables,
    TContext
> => {
    return useMutation(useSolveCornerPlacementMutationOptions(options), queryClient);
};
/**
 * Solve automatic Frami clamp placement for lateral panel joints on a given wall side
 */
export const useSolveConnectorPlacementHook = () => {
    const solveConnectorPlacement = useCustomAppClient<SolveConnectorPlacementResponse>();

    return useCallback(
        (solveConnectorPlacementModel: SolveConnectorPlacementModel, signal?: AbortSignal) => {
            return solveConnectorPlacement({
                url: `/forming/solve-connector-placement`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: solveConnectorPlacementModel,
                signal
            });
        },
        [solveConnectorPlacement]
    );
};

export const getSolveConnectorPlacementMutationKey = () => ['solveConnectorPlacement'] as const;

export const useSolveConnectorPlacementMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useSolveConnectorPlacementHook>>>,
        TError,
        SolveConnectorPlacementMutationVariables,
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSolveConnectorPlacementHook>>>,
    TError,
    SolveConnectorPlacementMutationVariables,
    TContext
> => {
    const mutationKey = getSolveConnectorPlacementMutationKey();
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const solveConnectorPlacement = useSolveConnectorPlacementHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useSolveConnectorPlacementHook>>>,
        SolveConnectorPlacementMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return solveConnectorPlacement(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type SolveConnectorPlacementMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useSolveConnectorPlacementHook>>>
>;
export type SolveConnectorPlacementMutationBody = SolveConnectorPlacementModel;
export type SolveConnectorPlacementMutationError = ErrorType<ProblemDetails>;
export type SolveConnectorPlacementMutationVariables = { data: SolveConnectorPlacementModel };

export const useSolveConnectorPlacement = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useSolveConnectorPlacementHook>>>,
            TError,
            SolveConnectorPlacementMutationVariables,
            TContext
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useSolveConnectorPlacementHook>>>,
    TError,
    SolveConnectorPlacementMutationVariables,
    TContext
> => {
    return useMutation(useSolveConnectorPlacementMutationOptions(options), queryClient);
};

export type SolveAnchorPlacementMutationVariables = { data: SolveAnchorPlacementModel };

export const useSolveAnchorPlacementHook = () => {
    const solveAnchorPlacement = useCustomAppClient<SolveAnchorPlacementResponse>();

    return useCallback(
        (model: SolveAnchorPlacementModel, signal?: AbortSignal) =>
            solveAnchorPlacement({
                url: '/forming/solve-anchor-placement',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: model,
                signal
            }),
        [solveAnchorPlacement]
    );
};

export const useSolveAnchorPlacement = (
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useSolveAnchorPlacementHook>>>,
            ErrorType<ProblemDetails>,
            SolveAnchorPlacementMutationVariables
        >;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useSolveAnchorPlacementHook>>>,
    ErrorType<ProblemDetails>,
    SolveAnchorPlacementMutationVariables
> => {
    const solveAnchorPlacement = useSolveAnchorPlacementHook();
    return useMutation(
        {
            mutationKey: ['solveAnchorPlacement'],
            mutationFn: ({ data }) => solveAnchorPlacement(data),
            ...options?.mutation
        },
        queryClient
    );
};
